import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./ThroughlinePaywall.module.scss"

/**
 * ThroughlinePaywall (`/throughline`) — an original, self-contained
 * NON-ONBOARDING paywall for Private Photo Vault Premium. Paywall type rolled at
 * random for this run: NON-ONBOARDING (a mid-app upsell, not first-launch, not
 * expiration).
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Your memories are shown as a scattered CONSTELLATION of photo-stars floating
 * alone in the dark. In the free vault each one is a single copy behind one
 * basic lock — unlinked, un-guarded, drifting apart. You DRAW A SINGLE
 * CONNECTING LINE through them (the same gesture as setting a pattern lock);
 * as the line reaches each star it snaps into place, brightens from cold amber
 * to brand blue, and one real Premium guard ignites along the bottom. When the
 * throughline is complete every memory is bound into one guarded whole and the
 * whole field lights up.
 *
 * ── Directive (active standing directive this run — NOT reset to default) ───
 * "build paywalls centered around loss aversion, use fear as the driving factor.
 *  Include some amazing animations, highlight our feature list. Target new users."
 * Honored: the fear is loss aversion on the UNGUARDED, ALONE, single-copy state
 * a brand-new free user is already in ("right now, each one is on its own") —
 * never a threat that photos are deleted. The amazing animation is the drawn
 * light-throughline binding drifting photo-stars into a guarded constellation,
 * with guards igniting one by one. Five REAL Premium guards are the spine of the
 * layout (highlight our feature list). Framed for a NEW user who has just begun
 * adding memories.
 *
 * ── Divergence (see claude-paywalls/_ledger.md) ────────────────────────────
 * Layout      : a flat, luminous MEMORY-CONSTELLATION / node-graph — photo-stars
 *               on a dark field joined by a drawn light-line, guards as a lit
 *               spine below. Not hero+card, calendar, road, net, arch, board,
 *               quilt, deck, music box, postmark, scope, carryall, control
 *               console, or Vigil's vital-sign monitor. Flat / data-viz /
 *               photographic art direction — the untouched axis in the ledger.
 * Interaction : DRAW-THE-THROUGHLINE — one continuous line traced through the
 *               photo-stars in sequence (mirrors the app's real pattern-lock
 *               gesture), each star locking + a guard igniting. Distinct from
 *               hold, drag-divider, rotary, haul, flip, stamp, zip, master-
 *               switch, rhythmic-tap, trace-a-ring (single circle) and lasso
 *               (free enclosing loop) — this is a sequential connect-through.
 * Register    : tender protectiveness with a quiet unease at the scattered/alone
 *               state — not cold-urgency, not clinical console, not warm monitor.
 * Value frame : CONNECTION / binding-into-one — scattered & exposed → linked &
 *               guarded.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * All five guards are real Premium features (Cloud Vault backup, Break-In
 * Report photo & location, Trash Recovery 30-day, Decoy Passcode, App Disguise).
 * The fear is the present unguarded state, never data loss: explicit copy that
 * photos never leave the vault and that drawing the line only turns guards on —
 * it never moves or deletes anything. No countdown, no fake scarcity; real
 * savings arithmetic from live product data. Yearly is the featured, default,
 * most-prominent price; monthly is small and secondary. Sample images are
 * labelled Unsplash demo photos, not the user's own library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// Photo-stars: everyday memories a brand-new user might have just added.
// Reuses bundled Unsplash demo photos already in the repo (no external egress).
// Positions are in % of the stage; the same coords drive both the HTML star and
// the drawn connecting line, so they always stay aligned.
const STARS = [
  { src: "/recall/coffee-1.jpg", tag: "Sat morning", x: 17, y: 24 },
  { src: "/recall/dog-1.jpg", tag: "Biscuit", x: 42, y: 13 },
  { src: "/recall/beach-1.jpg", tag: "Golden hour", x: 66, y: 20 },
  { src: "/recall/sunset-1.jpg", tag: "The drive", x: 78, y: 49 },
  { src: "/recall/mountain-1.jpg", tag: "Ridge trail", x: 57, y: 60 },
  { src: "/recall/flower-1.jpg", tag: "Her garden", x: 31, y: 67 },
  { src: "/recall/city-1.jpg", tag: "First apartment", x: 16, y: 51 },
]

// The five real Premium guards — each ignites as the line binds one more memory.
const GUARDS = [
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    name: "Cloud Vault",
    line: "a private, encrypted second copy",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Report",
    line: "photo + location on a wrong PIN",
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Trash Recovery",
    line: "a 30-day safety net for deletes",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Passcode",
    line: "a false vault for prying eyes",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App Disguise",
    line: "hide the vault behind another icon",
  },
]

// number of connections you can draw = STARS - 1; guards map onto the first 5.
const LINKS = STARS.length - 1

const ThroughlinePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = premium?.hasTrial ?? yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // linked = how many connections have been drawn (0..LINKS). linked memories =
  // linked + 1 (the head star counts once the first link lands... we treat the
  // first star as already "present" and count bound = linked when > 0).
  const [linked, setLinked] = useState(0)
  const bound = linked >= LINKS

  const stageRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  // measure the stage so we can lay the connecting segments out in real pixels
  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el) return
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    measure()
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure)
      ro.observe(el)
    } else {
      window.addEventListener("resize", measure)
    }
    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener("resize", measure)
    }
  }, [])

  // precompute each segment's geometry (px) from the % star coords
  const segments = useMemo(() => {
    const segs: { left: number; top: number; len: number; angle: number }[] = []
    for (let i = 0; i < LINKS; i++) {
      const a = STARS[i]
      const b = STARS[i + 1]
      const ax = (a.x / 100) * size.w
      const ay = (a.y / 100) * size.h
      const bx = (b.x / 100) * size.w
      const by = (b.y / 100) * size.h
      const dx = bx - ax
      const dy = by - ay
      segs.push({
        left: ax,
        top: ay,
        len: Math.hypot(dx, dy),
        angle: (Math.atan2(dy, dx) * 180) / Math.PI,
      })
    }
    return segs
  }, [size])

  // ── the "connect one more" action, shared by auto-teach, drag and keyboard ──
  const userTook = useRef(false)
  const connectOne = useCallback(() => {
    setLinked((n) => clamp(n + 1, 0, LINKS))
  }, [])

  // auto-teach: shortly after mount, draw the whole line so the concept reads at
  // a glance (and the resting state we sell is the bound, guarded constellation).
  // A real drag by the user cancels it. Reduced motion → jump straight to bound.
  useEffect(() => {
    if (reduceMotion) {
      setLinked(LINKS)
      return
    }
    const timers: number[] = []
    const start = window.setTimeout(() => {
      for (let i = 0; i < LINKS; i++) {
        timers.push(
          window.setTimeout(() => {
            if (!userTook.current) connectOne()
          }, i * 460),
        )
      }
    }, 900)
    timers.push(start)
    return () => timers.forEach((t) => clearTimeout(t))
  }, [reduceMotion, connectOne])

  // ── draw interaction: dragging the pointer through the stars connects them in
  // sequence. Passing within reach of the next unconnected star lands the link.
  const drawing = useRef(false)
  const linkedRef = useRef(0)
  linkedRef.current = linked

  const pointerAt = useCallback(
    (clientX: number, clientY: number) => {
      const el = stageRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const px = ((clientX - r.left) / r.width) * 100
      const py = ((clientY - r.top) / r.height) * 100
      // if the pointer is near the next star in the chain, connect it.
      const nextIdx = linkedRef.current + 1
      if (nextIdx >= STARS.length) return
      const next = STARS[nextIdx]
      const near = Math.hypot(px - next.x, py - next.y) < 13
      if (near) connectOne()
    },
    [connectOne],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      userTook.current = true
      drawing.current = true
      // a fresh drag restarts the thread from the beginning
      setLinked(0)
      try {
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      } catch {}
      pointerAt(e.clientX, e.clientY)
    },
    [pointerAt],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drawing.current) return
      pointerAt(e.clientX, e.clientY)
    },
    [pointerAt],
  )
  const endDraw = useCallback(() => {
    drawing.current = false
  }, [])

  // keyboard / assistive: a button steps the thread forward one memory at a time
  const onConnectKey = useCallback(() => {
    userTook.current = true
    if (bound) setLinked(0)
    else connectOne()
  }, [bound, connectOne])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const boundCount = linked + (linked > 0 ? 1 : 0) // memories bound so far

  return (
    <div className={`${styles.root} ${bound ? styles.rootBound : ""}`}>
      <div className={styles.aura} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro — fear-led, honest: the scattered / alone state */}
      <section className={styles.intro}>
        <h1 className={styles.title}>
          {bound ? (
            <>
              Every memory,
              <br />
              <span className={styles.titleAccent}>bound and guarded.</span>
            </>
          ) : (
            <>
              Right now, each one
              <br />
              <span className={styles.titleAccent}>is on its own.</span>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          {bound
            ? "The line holds them together — a single copy becomes a guarded one, watched from every side."
            : "Each memory you add sits here as a single copy behind one basic lock — unlinked and unguarded. Draw the line to bring every guard around them."}
        </p>
      </section>

      {/* ── the memory-constellation stage ──────────────────────────────── */}
      <section className={styles.stageWrap}>
        <div
          ref={stageRef}
          className={styles.stage}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDraw}
          onPointerCancel={endDraw}
          onPointerLeave={endDraw}
          role="img"
          aria-label={
            bound
              ? "All memories connected and guarded"
              : "Scattered memories waiting to be connected"
          }
        >
          {/* the drawn throughline — one segment per connection */}
          <div className={styles.links} aria-hidden="true">
            {segments.map((s, i) => {
              const on = i < linked
              return (
                <span
                  key={i}
                  className={`${styles.link} ${on ? styles.linkOn : ""}`}
                  style={{
                    left: `${s.left}px`,
                    top: `${s.top}px`,
                    width: `${s.len}px`,
                    transform: `rotate(${s.angle}deg) scaleX(${on ? 1 : 0})`,
                    transitionDelay: on ? "0ms" : "0ms",
                  }}
                />
              )
            })}
            {/* travelling glow that sits at the head of the drawn line */}
            {linked > 0 && !bound && segments[linked - 1] && (
              <span
                className={styles.comet}
                style={{
                  left: `${(STARS[linked].x / 100) * size.w}px`,
                  top: `${(STARS[linked].y / 100) * size.h}px`,
                }}
              />
            )}
          </div>

          {/* the photo-stars */}
          {STARS.map((star, i) => {
            const lit = i <= linked
            return (
              <figure
                key={star.src}
                className={`${styles.star} ${lit ? styles.starOn : ""}`}
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  ["--d" as any]: `${i * 0.5}s`,
                }}
              >
                <span className={styles.starHalo} aria-hidden="true" />
                <span className={styles.starFrame}>
                  <img src={star.src} alt="" draggable={false} />
                </span>
                <figcaption className={styles.starTag}>{star.tag}</figcaption>
              </figure>
            )
          })}
        </div>

        {/* draw affordance / progress */}
        <div className={styles.stageFoot}>
          <button
            type="button"
            className={styles.drawBtn}
            onClick={onConnectKey}
            aria-label={
              bound ? "Trace the line again" : "Connect the next memory"
            }
          >
            <span className={styles.drawDot} aria-hidden="true" />
            {bound
              ? "Trace it again"
              : linked === 0
                ? "Draw the line through them"
                : `Keep going — ${LINKS - linked} to go`}
          </button>
          <span className={styles.progress} aria-live="polite">
            {bound ? "All 7 linked · 5 guards on" : `${boundCount}/7 linked`}
          </span>
        </div>
      </section>

      {/* ── the five real guards — the spine, igniting one per link ──────── */}
      <ul className={styles.guards}>
        {GUARDS.map((g, i) => {
          const on = linked > i
          const Icon = g.Icon
          return (
            <li
              key={g.key}
              className={`${styles.guard} ${on ? styles.guardOn : ""}`}
              style={{ ["--i" as any]: i }}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardState} aria-hidden="true">
                {on ? (
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <path
                      d="M5 12.6l4.3 4.3L19 7.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className={styles.guardOff} />
                )}
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Your photos never leave your vault. Drawing the line only turns the
        guards on — it never moves or deletes a thing.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Full guard · Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Draw every guard around them — free"
                : "Draw every guard around them"
              : "Guard them monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default ThroughlinePaywall
